import { VIEW_MODE, LIST_TYPE, PARAM_PAGE, FILTER } from '@/components/Lobby/enum.js'
import { createState } from '~/util/storeHelpers'

const sectionList = createState({ key: 'sectionList', initialValue: null })
const sectionViewLayout = createState({ key: 'sectionViewLayout', initialValue: null })
const sectionViewList = createState({ key: 'sectionViewList', initialValue: null })
const subsectionItem = createState({ key: 'subsectionItem', initialValue: null })
const providerItem = createState({ key: 'providerItem', initialValue: null })
const collectionItem = createState({ key: 'collectionItem', initialValue: null })
const favoritesMetaList = createState({ key: 'favoritesMetaList', initialValue: [] })
const presetFilters = createState({ key: 'presetFilters', initialValue: null })

const actionHelper = async ({ commit, getters }, { term, take, page, payloadData, fieldType, displayMode, skipStore, options }, { name, apiMethod }) => {
  try {
    !skipStore && commit(`set_${name}`, { isLoading: true })
    const { data } = await apiMethod({ term, take, page, displayMode, options })

    if (payloadData) {
      const list = [ ...payloadData[fieldType]?.result, ...data[fieldType].result ]
      data[fieldType].result = list
    }

    !skipStore && commit(`set_${name}`, { data })
    return data
  }
  catch (e) {
    !skipStore && commit(`set_${name}`, { error: e })
  }
  finally {
    !skipStore && commit(`set_${name}`, { isLoading: false })
  }
}

const filterOptions = {
  isOpen: false,
  selected: {},
  term: '',
  isFiltered: false,
}

export const state = () => ({
  sectionList: sectionList.state,
  sectionViewLayout: sectionViewLayout.state,
  sectionViewList: sectionViewList.state,
  subsectionItem: subsectionItem.state,
  providerItem: providerItem.state,
  collectionItem: collectionItem.state,
  favoritesMetaList: favoritesMetaList.state,
  presetFilters: presetFilters.state,
  filterOptions,
  isOpenSearch: false,
})

export const mutations = {
  set_sectionList: sectionList.setter,
  set_sectionViewLayout: sectionViewLayout.setter,
  set_sectionViewList: sectionViewList.setter,
  set_subsectionItem: subsectionItem.setter,
  set_providerItem: providerItem.setter,
  set_collectionItem: collectionItem.setter,
  set_favoritesMetaList: favoritesMetaList.setter,
  set_presetFilters: presetFilters.setter,
  set_filterOptions: (state, { isOpen, selected, term, reset }) => {
    if (isOpen !== undefined) {
      state.filterOptions.isOpen = isOpen
    }
    if (selected) {
      state.filterOptions.selected = selected

      const filteredResult = Object.values(selected).reduce((acc, obj) => {
        return { ...acc, ...Object.fromEntries(Object.entries(obj).filter(([_, val]) => val)) }
      }, {})
      state.filterOptions.isFiltered = Object.keys(filteredResult).length
    }
    if (term) {
      state.filterOptions.term = term
    }
    if (reset) {
      state.filterOptions = { ...filterOptions }
    }
  },
  set_openSearch: (state, payload) => {
    state.isOpenSearch = payload
  },
}

export const actions = {
  async fetch_sectionList({ commit, getters }) {
    try {
      if (getters.get_sectionList.isLoading) return
      commit('set_sectionList', { isLoading: true })
      const { data } = await this.$api._games.getSectionList()
      commit('set_sectionList', { data })
    }
    catch (e) {
      commit('set_sectionList', { error: e })
    }
    finally {
      commit('set_sectionList', { isLoading: false })
    }
  },
  async fetch_sectionViewLayout({ commit, getters }, { term }) {
    try {
      if (getters.get_sectionViewLayout.isLoading) return
      commit('set_sectionViewLayout', { isLoading: true })
      const { data } = await this.$api._games.getSectionViewLayout({ term })
      commit('set_sectionViewLayout', { data })
    }
    catch (e) {
      commit('set_sectionViewLayout', { error: e })
    }
    finally {
      commit('set_sectionViewLayout', { isLoading: false })
    }
  },
  async fetch_sectionViewList({ commit, getters }, { term, take, page, payloadData, fieldType, skipStore }) {
    return await actionHelper(
      { commit, getters },
      { term, take, page, payloadData, fieldType, skipStore },
      { name: 'sectionViewList', apiMethod: this.$api._games.getSectionViewList }
    )
  },
  async fetch_subsectionItem({ commit, getters }, { term, take, page, payloadData, fieldType, skipStore, options }) {
    return await actionHelper(
      { commit, getters },
      { term, take, page, payloadData, fieldType, skipStore, options },
      { name: 'subsectionItem', apiMethod: this.$api._games.getSubsectionItem }
    )
  },
  async fetch_providerItem({ commit, getters }, { term, take, page, payloadData, displayMode, skipStore, options }) {
    return await actionHelper(
      { commit, getters },
      { term, take, page, payloadData, fieldType: LIST_TYPE.GAME.FIELD, displayMode, skipStore, options },
      { name: 'providerItem', apiMethod: this.$api._games.getProviderItem }
    )
  },
  async fetch_collectionItem({ commit, getters }, { term, take, page, payloadData, skipStore, options }) {
    return await actionHelper(
      { commit, getters },
      { term, take, page, payloadData, fieldType: LIST_TYPE.GAME.FIELD, skipStore, options },
      { name: 'collectionItem', apiMethod: this.$api._games.getCollectionItem }
    )
  },
  reset_sectionView({ commit }, { type } = {}) {
    if (VIEW_MODE.DEFAULT === type) {
      commit('set_sectionViewLayout', { reset: true })
    }
    if (VIEW_MODE.EXPAND === type) {
      commit('set_sectionViewList', { reset: true })
    }

    if (!type) {
      commit('set_sectionViewLayout', { reset: true })
      commit('set_sectionViewList', { reset: true })
    }
  },
  reset_subsectionItem({ commit }) {
    commit('set_subsectionItem', { reset: true })
  },
  reset_providerItem({ commit }) {
    commit('set_providerItem', { reset: true })
  },
  reset_collectionItem({ commit }) {
    commit('set_collectionItem', { reset: true })
  },
  async toggle_gameFavorites({ commit, getters }, { game, toggle }) {
    try {
      const { data } = await this.$api._games.toggleGameFavorites({
        game,
        method: toggle ? 'add' : 'remove',
      })

      const copyList = [...getters.get_favoritesMetaList.data]

      if (toggle) {
        copyList.push(data.term)
      }
      else {
        const findingIndex = copyList.findIndex((term) => data.term === term)

        if (findingIndex !== -1) {
          copyList.splice(findingIndex, 1)
        }
      }

      commit('set_favoritesMetaList', { data: copyList })
    }
    catch (e) {}
  },
  async fetch_gameFavorites({ commit }) {
    try {
      const { data } = await this.$api._games.favoritesMetaList()
      commit('set_favoritesMetaList', { data })
    }
    catch (e) {}
  },
  async fetch_presetFilters({ commit, getters }, { preset, type, mode, term, reset }) {
    if (reset) {
      commit('set_presetFilters', { reset: true })
      return
    }

    if (getters.get_presetFilters.isLoading) return

    try {
      commit('set_presetFilters', { isLoading: true })
      const { data } = await this.$api._games.getFilters({ preset, type, mode, term })

      data && commit('set_presetFilters', { data })
    }
    catch (e) {
      console.log(e)
      commit('set_presetFilters', { error: e })
    }
    finally {
      commit('set_presetFilters', { isLoading: false })
    }
  },
  write_filterOptions({ commit }, { isOpen, selected, term, reset }) {
    commit('set_filterOptions', { isOpen, selected, term, reset })
  },
  toggle_openSearch({ commit }, payload) {
    commit('set_openSearch', payload)
  },
}

export const getters = {
  get_sectionList: (state) => state.sectionList,
  get_sectionViewLayout: (state) => state.sectionViewLayout,
  get_sectionViewList: (state) => state.sectionViewList,
  get_subsectionItem: (state) => state.subsectionItem,
  get_providerItem: (state) => state.providerItem,
  get_collectionItem: (state) => state.collectionItem,
  get_favoritesMetaList: (state, _, __, rootGetters) => {
    if (!rootGetters['user/isLoggedIn']) return { data: [] }
    return state.favoritesMetaList
  },
  get_currentSection: (state) => {
    return (route) => {
      const sectionList = state.sectionList.data
      if (!Array.isArray(sectionList)) return

      const currentParam = route.params[PARAM_PAGE]
      return sectionList.find((el) => el.term === currentParam)
    }
  },
  get_presetFilters: (state) => state.presetFilters,
  get_filterOptions: (state) => state.filterOptions,
  get_filterType: (_, getters) => {
    return (route) => {
      let type = null

      switch (getters.get_currentSection(route)?.listType) {
      case LIST_TYPE.GAME.NAME:
        type = FILTER.TYPE.SUBSECTION
        break
      case LIST_TYPE.COLLECTION.NAME:
        type = FILTER.TYPE.COLLECTION
        break
      case LIST_TYPE.PROVIDER.NAME:
        type = FILTER.TYPE.PROVIDER
        break
      }

      return type
    }
  },
  isOpenSearch: (state) => state.isOpenSearch,
}
